import { ChevronLeft, TruckElectric } from "lucide-react";
import React, { Suspense, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Link, matchPath, Navigate, Route, Routes, useLocation } from "react-router";


import { Redirect } from "base/app";
import { IS_DEV } from "base/constants";
import EventTracker from "base/eventsTracker";
import { FileUploaderPage } from "base/file_uploader";
import "base/css/w3.css";
import "base/css/app.css";
import "./css/app.css";
import { Popup } from "base/ui/popups";
import { useBroadcastedState, broadcaster } from "base/utils/events";

import App from "./app";
import "base/utils/polyfills/objpolyfills";
import WhatsAppButton from "./components/features/widget/WhatsAppButton";
import WhatsappLoginButton from "./components/features/widget/WhatsappLogin";
import HeaderNav from "./components/layout/HeaderNav";
import { initBase, SuspenseLoading } from "./init";
import AreaRoutes from "./pages/AreaRoutes";
import DeliveryRoutesList from "./pages/DeliveryRoutesList";
import FreeTrialPage from "./pages/landing/FreeTrialPage";
import QRScan from "./pages/QRScan";
import RedirectToFlowCart from "./pages/RedirectToFlowCart";
import WooPluginUri from "./pages/WooPluginUri";
import { useStaffLocation } from "./useStaffLocation";
import WebScrappingPage from "./WebScrappingPage";


function NotFound() {
  return (
    <div className="tw-min-h-screen tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-white tw-text-center tw-px-4">
      <h1 className="tw-text-7xl tw-font-extrabold tw-text-gray-800">404</h1>
      <p className="tw-mt-4 tw-text-2xl tw-font-semibold tw-text-gray-700">Page not found</p>
      <p className="tw-mt-2 tw-text-gray-500">Sorry, the page you&apos;re looking for doesn&apos;t exist.</p>
      <Link
        to="/"
        className="tw-mt-8 btn-primary-v2"
      >
        Go Home
      </Link>
    </div>
  );
}

function Header() {
	/* only updated after init or other user loading call, deliberately set like that */
	const [shop_info] = useBroadcastedState("shop_info", "");
	const [user] = useBroadcastedState("_user_", null);

	useStaffLocation();

	const is_user_manager =
		user?.roles?.superadmin || user?.roles?.manager?.length || user?.roles?.delivery_org_manager?.length;

	useEffect(
		() => {
			// tracking mixpanel on shop
			if (shop_info.delivery_org_id && user?._id) {
				EventTracker.identify(user._id);
				// EventTracker.track("User detected");
				EventTracker.people.set({
					$name: user.name,
					$phone_number: user.phone_number,
					$delivery_org_id: shop_info.delivery_org_id,
					$user_roles: user.roles && Object.keys(user.roles).length ? Object.keys(user.roles) : [],
				});
			}
		}, [shop_info, user]
	);

	if(!shop_info) return null; // wait for shop_info to be loaded
	return (
		<div
			className={`tw-items-center tw-justify-between tw-z-10 tw-pl-1 tw-pr-4 tw-h-12 md:tw-h-14 tw-flex tw-bg-primary-bg" : ""}`}
		>
			{/* logo */}
			<div className="tw-flex items-center">
				{
					is_user_manager 
					? (
						<Link to={`/admin/${shop_info.delivery_org_id || ""}`} className="tw-px-2 tw-flex tw-items-center">
							<ChevronLeft />
						</Link>
					) : null
				}
				<div
					className="tw-ml-2 tw-whitespace-nowrap tw-flex tw-gap-1 tw-items-center tw-cursor-pointer"
				>
					{shop_info?.logo || <TruckElectric size={22} className="tw-text-md tw-text-gray-500 tw-mr-2" />}
					<span className="tw-capitalize tw-text-md md:tw-text-lg tw-font-bold tw-text-primary ">
						{shop_info.title || ""}
					</span>
				</div>
			</div>
			<HeaderNav />
		</div>
	);
}


const LazyWaConnectPage = React.lazy(() => import("./pages/WaConnect"));


function ShopAppPages() {
	return (
		<>
			<Header />
			<Routes>
				{/* main */}
				<Route path="/" element={<App />} exact={true} />
				<Route path="/:delivery_route_id" element={<App />} exact={true} />
				<Route path="/area" element={<AreaRoutes />} exact={true} />
				<Route path="/qr" element={<QRScan />} exact={true} />
				{/* file uploader */}
				<Route path="/upload" element={<FileUploaderPage />} exact={true} />
				<Route path="/tnc/*" element={<RedirectToFlowCart />} />
				<Route path="/sukhiba-whatsapp-plugin/*" element={<WooPluginUri />} />
				<Route path="/privacy_policy/*" element={<RedirectToFlowCart />} />
				{/* Customer Org Routes List Page */}
				<Route path="/dorg/:delivery_org_id" element={<DeliveryRoutesList />} />
				<Route path="/shops/:delivery_org_id" element={<DeliveryRoutesList />} />
				<Route path="/dorg" element={<DeliveryRoutesList />} />
				<Route path="/shops" element={<DeliveryRoutesList />} />
				<Route path="/not-found" element={<NotFound />} />
				<Route path="*" element={<Redirect if_match={/^\/(admin|delivery|staff|shopify)/} /> } />
			</Routes>
		</>
	);
}


const AppRoot = () => (
	<React.StrictMode>
		<BrowserRouter>
			<Routes>
				{
					// Landing pages
					[
						"/home",
						"/b2b",
						"/b2c",
						"/industries",
						"/sme",
						"/large-enterprises",
						"/partnerships",
						"/contactus",
						"/thankyou",
						"/pricing",
						"/customer-stories",
						"/customer-stories/*",
						"/bookdemo",
						"/whatsapp-ecommerce-store",
						"/whatsapp-shopify-store",
						"/whatsapp-woocommerce-store",
						"/bulk-whatsapp-message",
					].map((path) => (
						<Route
							key={path}
							path={path}
							element={
								<RedirectToFlowCart />
							}
						/>
					))
				}
				{
					// demo booking pages
					[
						"/signup",
					].map((path) => (
						<Route
							key={path}
							path={path}
							element={
								<Suspense fallback={<SuspenseLoading />}>
									<FreeTrialPage />
								</Suspense>
							}
						/>
					))
				}
				{
					/* default landing paths */
					window.location.hostname.endsWith("sukhiba.com") || IS_DEV 
					? (
						<Route
							path="/landing/*"
							element={
								<RedirectToFlowCart />
							}
							exact={true}
						/>
					) : (
						<Route path="/landing" element={<DeliveryRoutesList />} exact={true} />
					)
				}
				{/* WA Connect page */}
				<Route
					path="/wa-connect"
					element={
						<Suspense fallback={<SuspenseLoading />}>
							<LazyWaConnectPage />
						</Suspense>
					}
					exact={true}
				/>
				{/* widget pages */}
				<Route path="/whatsapp-button-generator" element={<WhatsAppButton />} />
				<Route path="/whatsapp-login" element={<WhatsappLoginButton />} />

				{/* Shop and delivery Pages */}
				<Route path="/*" element={<ShopAppPages />} />
				
				{ /* redirect to admin for  */
					IS_DEV && ["/admin/*", "/delivery/*", "/staff/*"].map(
						(path) => <Route
							key={path}
							path={path}
							element={<Redirect to="/admin-index.html" />} 
						/>
					)
				}
				{ /* redirect to admin for  */
					IS_DEV && ["/lite_checkout/widget"].map(
						(path) => <Route
							key={path}
							path={path}
							element={<Redirect to="/lite-checkout-widget-index.html" />} 
						/>
					)
				}
				{ /* redirect to admin for  */
					IS_DEV && ["/lite_checkout/*"].map(
						(path) => <Route
							key={path}
							path={path}
							element={<Redirect to="/lite-checkout-index.html" />} 
						/>
					)
				}
				{ /* shopify redirects  */
					IS_DEV && ["widget", "admin", "checkout"].flatMap(
						(action) => [<Route
								key={action}
								path={`/shopify/${action}`}
								element={<Redirect to={`/shopify-${action}-index.html`} />} 
							/>,
							<Route
								key={`${action}-wildcard`}
								path={`/shopify/${action}/*`}
								element={<Redirect to={`/shopify-${action}-index.html`} />} 
							/>
						]
					)
				}
				{
					/* games */
					IS_DEV && ["scratch_card", "spin_the_wheel"].flatMap((game) => {
						return <Route 
							key={game}
							path={`/game/${game}/*`}	
							element={<Redirect to={`/${game.replaceAll("_", "-")}-index.html`} />}
						/>
					})
				}
				{ /* payments */
					IS_DEV && ["stripe"].flatMap((pay_type) => {
						return <Route
							key={pay_type}
							path={`/pay/${pay_type}`}
							element={<Redirect to={`/${pay_type}-payment-index.html`} />}
						/>
					})
				}
				{ /* woocommerce redirects  */
					IS_DEV && ["admin"].flatMap(
						(action) => [<Route
								key={action}
								path={`/woocommerce/${action}`}
								element={<Redirect to={`/woocommerce-${action}-index.html`} />} 
							/>,
							<Route
								key={`${action}-wildcard`}
								path={`/woocommerce/${action}/*`}
								element={<Redirect to={`/woocommerce-${action}-index.html`} />} 
							/>
						]
					)
				}
				<Route path="/try-flowcart" element={<WebScrappingPage />} />
			</Routes>
			<Popup />
		</BrowserRouter>
	</React.StrictMode>
);


const root_element = document.getElementById("root");
const root = ReactDOM.createRoot(root_element);
root.render(<AppRoot />);  // default

initBase();

